import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Search, RefreshCw, Trash2, FileSpreadsheet, Settings, Send, CheckCircle2, Pencil, TrendingUp, TrendingDown, Minus, IndianRupee, Printer, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api, getToken, setToken, API } from "@/lib/api";

const STATUS_COLORS = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  confirmed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  completed: "bg-sky-500/20 text-sky-400 border-sky-500/40",
  cancelled: "bg-rose-500/20 text-rose-400 border-rose-500/40",
};

const formatINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "0";
  try {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
  } catch {
    return String(Math.round(Number(n)));
  }
};

const MonthlyCard = ({ label, value, trend, emphasise, invertColor, asCount, divider, testId }) => {
  const showTrend = trend !== null && trend !== undefined;
  const flat = showTrend && trend === 0;
  const isUp = showTrend && trend > 0;
  let trendGood = isUp;
  if (invertColor && showTrend) trendGood = trend < 0;
  const Arrow = flat ? Minus : isUp ? TrendingUp : TrendingDown;
  const trendColor = flat
    ? "text-neutral-400"
    : trendGood
    ? "text-emerald-400"
    : "text-rose-400";
  return (
    <div
      data-testid={testId}
      className={`p-6 ${divider ? "md:border-r border-[color:var(--admin-border)]" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`mt-2 flex items-baseline gap-2 font-display ${emphasise ? "text-4xl" : "text-3xl"} ${emphasise ? "text-[color:var(--admin-amber)]" : "text-white"}`}>
        {!asCount && <IndianRupee className="h-5 w-5 opacity-70" />}
        <span>{asCount ? String(value ?? 0) : formatINR(value)}</span>
      </div>
      {showTrend ? (
        <div className={`mt-2 inline-flex items-center gap-1 text-xs ${trendColor}`}>
          <Arrow className="h-3.5 w-3.5" />
          <span>{flat ? "no change" : `${trend > 0 ? "+" : ""}${trend}% vs last month`}</span>
        </div>
      ) : (
        <div className="mt-2 text-xs text-neutral-600">no last-month data</div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", trip_type: "all", q: "" });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInfo, setSettingsInfo] = useState({ whatsapp_apikey_set: false, whatsapp_apikey_masked: "", resend_apikey_set: false, resend_apikey_masked: "", business_whatsapp: "", business_email: "", sender_email: "" });
  const [waKey, setWaKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [testingWa, setTestingWa] = useState(false);
  const [resendKey, setResendKey] = useState("");
  const [savingResend, setSavingResend] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [finOpen, setFinOpen] = useState(false);
  const [finBooking, setFinBooking] = useState(null);
  const [finForm, setFinForm] = useState({ kilometers: "", price_quoted: "", actual_expenses: "" });
  const [finSaving, setFinSaving] = useState(false);
  const [monthly, setMonthly] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    void refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.trip_type !== "all") params.trip_type = filters.trip_type;
      if (filters.q) params.q = filters.q;
      const [b, s] = await Promise.all([
        api.get("/admin/bookings", { params }),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      try {
        const m = await api.get("/admin/stats/monthly");
        setMonthly(m.data);
      } catch {
        /* monthly failure is non-fatal */
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken(null);
        nav("/admin/login");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status updated");
      void refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      void refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadReport = async (fmt) => {
    try {
      const qs = new URLSearchParams();
      if (filters.status !== "all") qs.set("status", filters.status);
      if (filters.trip_type !== "all") qs.set("trip_type", filters.trip_type);
      if (filters.q) qs.set("q", filters.q);
      const suffix = fmt === "xlsx" ? "xlsx" : "csv";
      const url = `${API}/admin/bookings/export.${suffix}${qs.toString() ? "?" + qs.toString() : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `bawajii-bookings-${stamp}.${suffix}`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success(`${suffix.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  const logout = () => {
    setToken(null);
    nav("/admin/login");
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    setWaKey("");
    try {
      const res = await api.get("/admin/settings");
      setSettingsInfo(res.data);
    } catch {
      toast.error("Could not load settings");
    }
  };

  const saveSettings = async () => {
    if (!waKey.trim()) {
      toast.error("Please paste your CallMeBot API key");
      return;
    }
    setSavingKey(true);
    try {
      await api.put("/admin/settings", { whatsapp_apikey: waKey.trim() });
      toast.success("WhatsApp key saved");
      const res = await api.get("/admin/settings");
      setSettingsInfo(res.data);
      setWaKey("");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingKey(false);
    }
  };

  const testWhatsApp = async () => {
    setTestingWa(true);
    try {
      const res = await api.post("/admin/whatsapp/test");
      if (res.data.ok) {
        toast.success("Test sent — check your WhatsApp!");
      } else {
        toast.error(`CallMeBot said: ${res.data.body?.slice(0, 120) || "unknown"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTestingWa(false);
    }
  };

  const saveResend = async () => {
    if (!resendKey.trim()) {
      toast.error("Please paste your Resend API key (starts with re_)");
      return;
    }
    setSavingResend(true);
    try {
      await api.put("/admin/settings", { resend_api_key: resendKey.trim() });
      toast.success("Resend key saved");
      const res = await api.get("/admin/settings");
      setSettingsInfo(res.data);
      setResendKey("");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingResend(false);
    }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await api.post("/admin/email/test");
      if (res.data.ok) {
        toast.success(`Test email sent to ${settingsInfo.business_email || "your inbox"}`);
      } else {
        toast.error("Email send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email test failed");
    } finally {
      setTestingEmail(false);
    }
  };

  const changePassword = async () => {
    if (!pwCurrent || !pwNew || !pwConfirm) {
      toast.error("Please fill all three password fields");
      return;
    }
    if (pwNew.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Password changed. Use the new password next time you log in.");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setPwSaving(false);
    }
  };

  const openFinancials = (b) => {
    setFinBooking(b);
    setFinForm({
      kilometers: b.kilometers ?? "",
      price_quoted: b.price_quoted ?? "",
      actual_expenses: b.actual_expenses ?? "",
    });
    setFinOpen(true);
  };

  const saveFinancials = async () => {
    if (!finBooking) return;
    setFinSaving(true);
    const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    try {
      await api.patch(`/admin/bookings/${finBooking.id}`, {
        kilometers: toNum(finForm.kilometers),
        price_quoted: toNum(finForm.price_quoted),
        actual_expenses: toNum(finForm.actual_expenses),
      });
      toast.success("Financials saved");
      setFinOpen(false);
      void refresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setFinSaving(false);
    }
  };

  const finProfit = () => {
    const q = Number(finForm.price_quoted);
    const a = Number(finForm.actual_expenses);
    if (!isFinite(q) || !isFinite(a) || finForm.price_quoted === "" || finForm.actual_expenses === "") return null;
    return q - a;
  };

  const downloadReceipt = async (booking) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${booking.id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const safe = (booking.name || "customer").replace(/\s+/g, "_");
      a.download = `bawajii-receipt-${safe}-${booking.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success("Receipt downloaded. Share it on WhatsApp!");
    } catch {
      toast.error("Could not generate receipt");
    }
  };

  const statCards = useMemo(
    () => [
      { label: "Total", value: stats?.total ?? 0, id: "stat-total" },
      { label: "Pending", value: stats?.pending ?? 0, id: "stat-pending" },
      { label: "Confirmed", value: stats?.confirmed ?? 0, id: "stat-confirmed" },
      { label: "Completed", value: stats?.completed ?? 0, id: "stat-completed" },
      { label: "Cancelled", value: stats?.cancelled ?? 0, id: "stat-cancelled" },
    ],
    [stats]
  );

  return (
    <div className="min-h-screen bg-[color:var(--admin-bg)] text-[color:var(--admin-text)]">
      <header className="border-b border-[color:var(--admin-border)]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand-link" className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Bawajii" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <div className="font-display text-lg">Bawajii Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400">Bookings dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={openSettings} data-testid="settings-btn" className="rounded-none border-[color:var(--admin-border)] bg-transparent text-white hover:bg-white/5">
              <Settings className="h-4 w-4 mr-2" /> Settings
            </Button>
            <Button variant="outline" onClick={refresh} data-testid="refresh-btn" className="rounded-none border-[color:var(--admin-border)] bg-transparent text-white hover:bg-white/5">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button onClick={() => downloadReport("xlsx")} data-testid="export-xlsx-btn" className="rounded-none bg-emerald-500 text-black hover:bg-emerald-400">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel Report
            </Button>
            <Button onClick={() => downloadReport("csv")} data-testid="export-csv-btn" className="rounded-none bg-[color:var(--admin-amber)] text-black hover:bg-amber-400">
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
            <Button variant="outline" onClick={logout} data-testid="logout-btn" className="rounded-none border-[color:var(--admin-border)] bg-transparent text-white hover:bg-white/5">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
        {/* MONTHLY PROFIT SUMMARY */}
        {monthly && (
          <section data-testid="monthly-summary" className="mb-6">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Revenue pulse · {monthly.current.month}</div>
                <div className="font-display text-2xl text-white">This month at a glance</div>
              </div>
              <div className="text-xs text-neutral-500">vs {monthly.previous.month}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-[color:var(--admin-border)]">
              <MonthlyCard
                label="Quoted"
                value={monthly.current.quoted}
                trend={monthly.trend.quoted_pct}
                testId="monthly-quoted"
                divider
              />
              <MonthlyCard
                label="Expenses"
                value={monthly.current.actual}
                trend={monthly.trend.actual_pct}
                invertColor
                testId="monthly-actual"
                divider
              />
              <MonthlyCard
                label="Net Profit"
                value={monthly.current.profit}
                trend={monthly.trend.profit_pct}
                emphasise
                testId="monthly-profit"
                divider
              />
              <MonthlyCard
                label="Trips"
                value={monthly.current.trips}
                trend={monthly.trend.trips_pct}
                asCount
                testId="monthly-trips"
              />
            </div>
          </section>
        )}

        <section className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--admin-border)]">
          {statCards.map((s, i) => (
            <div key={s.id} data-testid={s.id} className={`p-6 ${i < statCards.length - 1 ? "border-r border-[color:var(--admin-border)]" : ""}`}>
              <div className="text-xs uppercase tracking-widest text-neutral-500">{s.label}</div>
              <div className="mt-2 font-display text-4xl text-white">{s.value}</div>
            </div>
          ))}
        </section>

        <section className="mt-6 border border-[color:var(--admin-border)] p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Search</label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <Input
                data-testid="search-input"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="name, phone, location…"
                className="pl-9 bg-[color:var(--admin-surface)] border-[color:var(--admin-border)] text-white rounded-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="status-filter" className="mt-1 min-w-[160px] bg-[color:var(--admin-surface)] border-[color:var(--admin-border)] text-white rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 text-white border-neutral-700">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Trip type</label>
            <Select value={filters.trip_type} onValueChange={(v) => setFilters((f) => ({ ...f, trip_type: v }))}>
              <SelectTrigger data-testid="trip-filter" className="mt-1 min-w-[160px] bg-[color:var(--admin-surface)] border-[color:var(--admin-border)] text-white rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 text-white border-neutral-700">
                <SelectItem value="all">All trips</SelectItem>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="outstation">Outstation</SelectItem>
                <SelectItem value="airport">Airport</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={refresh} data-testid="apply-filters-btn" className="rounded-none bg-white text-black hover:bg-neutral-200">Apply</Button>
        </section>

        <section className="mt-6 border border-[color:var(--admin-border)]">
          <Table data-testid="bookings-table">
            <TableHeader>
              <TableRow className="border-[color:var(--admin-border)] hover:bg-transparent">
                <TableHead className="text-neutral-400">Booking</TableHead>
                <TableHead className="text-neutral-400">Customer</TableHead>
                <TableHead className="text-neutral-400">Trip</TableHead>
                <TableHead className="text-neutral-400">Pickup → Drop</TableHead>
                <TableHead className="text-neutral-400">Date/Time</TableHead>
                <TableHead className="text-neutral-400">Vehicle</TableHead>
                <TableHead className="text-neutral-400">Status</TableHead>
                <TableHead className="text-neutral-400">Financials</TableHead>
                <TableHead className="text-neutral-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-neutral-500">Loading…</TableCell></TableRow>
              ) : bookings.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-neutral-500" data-testid="empty-bookings">No bookings yet</TableCell></TableRow>
              ) : (
                bookings.map((b) => (
                  <TableRow key={b.id} className="border-[color:var(--admin-border)] hover:bg-white/5" data-testid={`booking-row-${b.id}`}>
                    <TableCell className="font-mono text-[11px] text-neutral-400">{b.id.slice(0, 8)}</TableCell>
                    <TableCell>
                      <div className="text-white">{b.name}</div>
                      <div className="text-xs text-neutral-500">{b.phone}</div>
                    </TableCell>
                    <TableCell className="capitalize">{b.trip_type}</TableCell>
                    <TableCell className="text-sm">
                      <div>{b.pickup_location}</div>
                      {b.drop_location && <div className="text-neutral-500">→ {b.drop_location}</div>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {b.pickup_date}
                      <div className="text-neutral-500">{b.pickup_time}</div>
                    </TableCell>
                    <TableCell className="capitalize">{b.vehicle_type} · {b.passengers}</TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                        <SelectTrigger data-testid={`status-select-${b.id}`} className={`rounded-none border ${STATUS_COLORS[b.status] || ""} bg-transparent min-w-[130px]`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral-900 text-white border-neutral-700">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell data-testid={`financials-cell-${b.id}`} className="text-xs">
                      {b.kilometers != null || b.price_quoted != null || b.actual_expenses != null ? (
                        <div className="space-y-0.5 leading-tight">
                          <div className="text-neutral-300">{b.kilometers != null ? `${b.kilometers} km` : "— km"}</div>
                          <div className="text-neutral-400">Q: ₹{b.price_quoted ?? "—"} · A: ₹{b.actual_expenses ?? "—"}</div>
                          {b.price_quoted != null && b.actual_expenses != null && (
                            <div className={`font-semibold ${b.price_quoted - b.actual_expenses >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                              Profit: ₹{(b.price_quoted - b.actual_expenses).toFixed(0)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-600">not set</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadReceipt(b)} data-testid={`receipt-btn-${b.id}`} title="Download PDF receipt" className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10">
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openFinancials(b)} data-testid={`edit-financials-btn-${b.id}`} title="Edit financials" className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(b.id)} data-testid={`delete-btn-${b.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </section>
      </main>

      {/* SETTINGS DIALOG */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog" className="bg-neutral-900 text-white border-neutral-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">WhatsApp Auto-Notify</DialogTitle>
            <DialogDescription className="text-neutral-400">
              Paste the API key CallMeBot sent to your WhatsApp. Once saved, every new booking auto-sends a message to {settingsInfo.business_whatsapp ? `+${settingsInfo.business_whatsapp}` : "your number"}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-neutral-700 bg-neutral-800/60 p-4 text-sm space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--admin-amber)]">Activation steps</div>
              <ol className="list-decimal list-inside space-y-1 text-neutral-300">
                <li>Save +34 644 51 95 23 as <b>CallMeBot</b> in Contacts</li>
                <li>WhatsApp them: <span className="font-mono">I allow callmebot to send me messages</span></li>
                <li>Wait for the reply with your APIKEY (up to a few hours)</li>
                <li>Paste that key below and hit Save</li>
              </ol>
            </div>

            <div>
              <Label className="text-neutral-300">CallMeBot API Key</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  data-testid="whatsapp-key-input"
                  placeholder={settingsInfo.whatsapp_apikey_set ? "Enter a new key to replace the saved one" : "e.g. 1234567"}
                  value={waKey}
                  onChange={(e) => setWaKey(e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white rounded-none"
                />
                <Button onClick={saveSettings} disabled={savingKey} data-testid="save-whatsapp-key-btn" className="rounded-none bg-[color:var(--admin-amber)] text-black hover:bg-amber-400">
                  {savingKey ? "Saving…" : "Save"}
                </Button>
              </div>
              {settingsInfo.whatsapp_apikey_set && (
                <div className="mt-2 text-xs text-emerald-400 flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Saved key: <span className="font-mono">{settingsInfo.whatsapp_apikey_masked}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
              <div className="text-xs text-neutral-400">Send a test message to verify it works.</div>
              <Button onClick={testWhatsApp} disabled={testingWa || !settingsInfo.whatsapp_apikey_set} data-testid="test-whatsapp-btn" className="rounded-none bg-emerald-500 text-black hover:bg-emerald-400">
                <Send className="h-4 w-4 mr-2" /> {testingWa ? "Sending…" : "Send test"}
              </Button>
            </div>

            {/* RESEND EMAIL SECTION */}
            <div className="pt-4 mt-2 border-t border-neutral-800 space-y-3">
              <div>
                <div className="font-display text-lg text-white">Email Auto-Notify</div>
                <div className="text-xs text-neutral-400">Paste your Resend API key so every booking also emails {settingsInfo.business_email || "you"} automatically.</div>
              </div>
              <div className="rounded-md border border-neutral-700 bg-neutral-800/60 p-4 text-sm space-y-2">
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--admin-amber)]">Where to get your key</div>
                <ol className="list-decimal list-inside space-y-1 text-neutral-300">
                  <li>Go to <span className="font-mono">resend.com</span> → Sign up (free tier is fine)</li>
                  <li>Dashboard → <b>API Keys</b> → <b>Create API Key</b></li>
                  <li>Copy the key (starts with <span className="font-mono">re_</span>) and paste below</li>
                </ol>
                <div className="text-[11px] text-neutral-500 pt-1">
                  Sender: <span className="font-mono">{settingsInfo.sender_email || "onboarding@resend.dev"}</span> (default works for testing; use your own domain for production).
                </div>
              </div>

              <div>
                <Label className="text-neutral-300">Resend API Key</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    data-testid="resend-key-input"
                    placeholder={settingsInfo.resend_apikey_set ? "Enter a new key to replace the saved one" : "re_XXXXXXXXXXXXXXXX"}
                    value={resendKey}
                    onChange={(e) => setResendKey(e.target.value)}
                    className="bg-neutral-800 border-neutral-700 text-white rounded-none"
                  />
                  <Button onClick={saveResend} disabled={savingResend} data-testid="save-resend-key-btn" className="rounded-none bg-[color:var(--admin-amber)] text-black hover:bg-amber-400">
                    {savingResend ? "Saving…" : "Save"}
                  </Button>
                </div>
                {settingsInfo.resend_apikey_set && (
                  <div className="mt-2 text-xs text-emerald-400 flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Saved key: <span className="font-mono">{settingsInfo.resend_apikey_masked}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-400">Send a test email to {settingsInfo.business_email || "your inbox"}.</div>
                <Button onClick={testEmail} disabled={testingEmail || !settingsInfo.resend_apikey_set} data-testid="test-email-btn" className="rounded-none bg-emerald-500 text-black hover:bg-emerald-400">
                  <Send className="h-4 w-4 mr-2" /> {testingEmail ? "Sending…" : "Send test"}
                </Button>
              </div>
            </div>

            {/* CHANGE PASSWORD SECTION */}
            <div className="pt-4 mt-2 border-t border-neutral-800 space-y-3">
              <div>
                <div className="font-display text-lg text-white flex items-center gap-2"><Lock className="h-4 w-4 text-[color:var(--admin-amber)]" /> Change Password</div>
                <div className="text-xs text-neutral-400">Rotate your admin password any time. Take effect on next login.</div>
              </div>
              <div className="grid gap-2">
                <div>
                  <Label className="text-neutral-300">Current password</Label>
                  <Input
                    type="password"
                    data-testid="pw-current-input"
                    value={pwCurrent}
                    onChange={(e) => setPwCurrent(e.target.value)}
                    className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300">New password (min 8 chars)</Label>
                  <Input
                    type="password"
                    data-testid="pw-new-input"
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                    className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300">Confirm new password</Label>
                  <Input
                    type="password"
                    data-testid="pw-confirm-input"
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
                  />
                </div>
                <div className="flex items-center justify-end pt-1">
                  <Button onClick={changePassword} disabled={pwSaving} data-testid="change-password-btn" className="rounded-none bg-[color:var(--admin-amber)] text-black hover:bg-amber-400">
                    <Lock className="h-4 w-4 mr-2" /> {pwSaving ? "Updating…" : "Update password"}
                  </Button>
                </div>
              </div>
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-none border-neutral-700 bg-transparent text-white hover:bg-white/5">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FINANCIALS DIALOG */}
      <Dialog open={finOpen} onOpenChange={setFinOpen}>
        <DialogContent data-testid="financials-dialog" className="bg-neutral-900 text-white border-neutral-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Trip financials</DialogTitle>
            <DialogDescription className="text-neutral-400">
              {finBooking ? (
                <>
                  {finBooking.name} · {finBooking.trip_type?.toString().toUpperCase()} · {finBooking.pickup_location}
                  {finBooking.drop_location ? ` → ${finBooking.drop_location}` : ""} · {finBooking.pickup_date}
                </>
              ) : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="fin-km" className="text-neutral-300">Kilometers</Label>
              <Input
                id="fin-km"
                type="number"
                min="0"
                step="0.1"
                data-testid="fin-km-input"
                value={finForm.kilometers}
                onChange={(e) => setFinForm((f) => ({ ...f, kilometers: e.target.value }))}
                placeholder="e.g. 320"
                className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="fin-q" className="text-neutral-300">Price Quoted (₹)</Label>
              <Input
                id="fin-q"
                type="number"
                min="0"
                step="1"
                data-testid="fin-quoted-input"
                value={finForm.price_quoted}
                onChange={(e) => setFinForm((f) => ({ ...f, price_quoted: e.target.value }))}
                placeholder="e.g. 6500"
                className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="fin-a" className="text-neutral-300">Actual Expenses (₹)</Label>
              <Input
                id="fin-a"
                type="number"
                min="0"
                step="1"
                data-testid="fin-actual-input"
                value={finForm.actual_expenses}
                onChange={(e) => setFinForm((f) => ({ ...f, actual_expenses: e.target.value }))}
                placeholder="e.g. 4200"
                className="mt-1 bg-neutral-800 border-neutral-700 text-white rounded-none"
              />
            </div>

            {finProfit() !== null && (
              <div data-testid="fin-profit-preview" className={`rounded-none border p-3 flex items-center justify-between ${finProfit() >= 0 ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
                <span className="text-xs uppercase tracking-widest text-neutral-300">Profit (auto)</span>
                <span className={`font-display text-2xl ${finProfit() >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  ₹ {finProfit().toFixed(0)}
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFinOpen(false)} className="rounded-none border-neutral-700 bg-transparent text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={saveFinancials} disabled={finSaving} data-testid="save-financials-btn" className="rounded-none bg-[color:var(--admin-amber)] text-black hover:bg-amber-400">
              {finSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
